import React, { useState } from "react";
import { Button, Modal, TextAreaField } from "../../../components/ui";
import AppointmentSummaryCard from "./AppointmentSummaryCard";
import type { ClientAppointment } from "../types/appointments.types";

export interface CompleteAppointmentModalProps {
  /** The appointment being marked — quoted so the press can be checked. */
  appointment: ClientAppointment;
  /** The client's first name, so the dialog says who the session was with. */
  firstName: string;
  /** Set while the answer is with the server. */
  busy?: boolean;
  onClose: () => void;
  /** @param note what was written down, or `""` where nothing was. */
  onConfirm: (note: string) => void;
}

/**
 * Says an appointment whose time has passed took place.
 *
 * One of the two answers such a row wants. The other — it did not happen — is
 * `CancelAppointmentModal`, because the backend keeps one state for an
 * appointment that did not take place however it failed to: called off in
 * advance, or nobody turned up. That pair used to be one dialog with a branch
 * in it; once the second half became an ordinary cancellation there was
 * nothing left to branch on.
 *
 * Asked before the status is written because nothing takes it back, and
 * because it is raised from an icon the width of a thumbnail in a table of
 * near-identical rows. The card in the middle is what makes a mis-press
 * recoverable — it says which row this is about while there is still a Cancel
 * to press.
 *
 * The note under it is the practitioner’s own record of what happened, and
 * it is optional and marked so: the button is not held closed by it, because a
 * session that took place took place whether or not there was time to write
 * about it. It is the mirror of the reason on `CancelAppointmentModal` — same
 * place in the dialog, same optional label, same box — because the two are
 * the two answers to one question and should not feel like different forms.
 */
const CompleteAppointmentModal: React.FC<CompleteAppointmentModalProps> = ({
  appointment,
  firstName,
  busy = false,
  onClose,
  onConfirm,
}) => {
  const [note, setNote] = useState("");

  return (
  <Modal
    open
    title="Mark this appointment as completed?"
    // The body draws its own heading at the size the design sets it; `title`
    // still names the dialog to a screen reader.
    hideTitle
    onClose={() => !busy && onClose()}
    className="max-w-[500px]"
    footer={
      <>
        <Button variant="secondary" size="md" disabled={busy} onClick={onClose}>
          Cancel
        </Button>
        <Button size="md" loading={busy} onClick={() => onConfirm(note)}>
          Mark completed
        </Button>
      </>
    }
  >
    <div className="flex flex-col gap-4">
      {/* `aria-hidden` because `hideTitle` does not drop the heading, it only
          takes it out of sight — the same words are already the dialog's
          accessible name, and this would be the second copy of them. */}
      <p
        aria-hidden="true"
        className="text-[20px] 3xl:text-[22px] font-medium text-ink"
      >
        Mark this appointment as completed?
      </p>

      <p className="text-[14px] 3xl:text-[15px] text-ink-muted">
        Confirm the session with{" "}
        <span className="font-medium text-ink">{firstName}</span> took place.
        It’ll be recorded as Completed.
      </p>

      <AppointmentSummaryCard appointment={appointment} />

      <div>
        <label
          htmlFor="appointment-completion-note"
          className="mb-1.5 block text-[14px] 3xl:text-[15px] font-medium text-ink"
        >
          Notes <span className="text-ink-muted">(optional)</span>
        </label>
        <TextAreaField
          id="appointment-completion-note"
          value={note}
          onValueChange={setNote}
          placeholder="Add a note for your records..."
          rows={4}
          disabled={busy}
        />
      </div>
    </div>
  </Modal>
  );
};

export default CompleteAppointmentModal;
